import { Injectable, NgZone, signal } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, Subject } from 'rxjs';
import { map, startWith, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { Network } from '@capacitor/network';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';

export interface NetworkStatus {
  connected: boolean;
  connectionType?: 'wifi' | 'cellular' | 'none' | 'unknown';
}

/** Polling interval (ms) for native: backup when networkStatusChange doesn't fire (e.g. WiFi off on Android). */
const NATIVE_POLL_INTERVAL_MS = 3000;
/** Poll navigator.onLine on native; often more reliable for airplane mode. */
const NAVIGATOR_ONLINE_POLL_MS = 2000;
/** Debounce ms to prevent duplicate event triggers. */
const DEBOUNCE_MS = 300;
/** Do not show offline popup for this long after app comes to foreground. Poll for network after this delay. */
const RESUME_GRACE_MS = 5000;

@Injectable({ providedIn: 'root' })
export class NetworkService {
  private readonly ngZone = new NgZone({ enableLongStackTrace: false });

  /** Primary source of truth for online/offline state. */
  private readonly onlineSubject = new BehaviorSubject<boolean>(true);
  private readonly isOnlineSignal = signal<boolean>(true);
  private readonly connectionTypeSignal = signal<'wifi' | 'cellular' | 'none' | 'unknown'>('unknown');
  private readonly statusSubject = new BehaviorSubject<NetworkStatus>({
    connected: true,
    connectionType: 'unknown',
  });

  private nativePollTimer: ReturnType<typeof setInterval> | null = null;
  private navigatorOnlinePollTimer: ReturnType<typeof setInterval> | null = null;
  private networkListenerHandle: PluginListenerHandle | null = null;
  private appListenerHandle: PluginListenerHandle | null = null;
  private lastBackgroundTime = 0;
  /** Set when app comes to foreground. Offline popup is suppressed until RESUME_GRACE_MS has passed. */
  private lastForegroundTime = 0;
  private resumePollTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly rawStatus$ = new Subject<boolean>();

  readonly online$: Observable<boolean>;
  readonly status$: Observable<NetworkStatus>;
  readonly isOnline = this.isOnlineSignal.asReadonly();
  readonly connectionType = this.connectionTypeSignal.asReadonly();

  constructor() {
    this.online$ = this.onlineSubject.pipe(distinctUntilChanged());
    this.status$ = this.statusSubject.asObservable();

    this.rawStatus$
      .pipe(debounceTime(DEBOUNCE_MS), distinctUntilChanged())
      .subscribe((connected) => {
        if (connected) {
          this.applyStatus(true, this.getConnectionTypeFromStatus());
        } else {
          const sinceForeground = this.lastForegroundTime > 0 ? Date.now() - this.lastForegroundTime : Infinity;
          if (sinceForeground < RESUME_GRACE_MS) {
            return;
          }
          this.applyStatus(false, this.getConnectionTypeFromStatus());
        }
      });

    void this.initialize();
  }

  private async initialize(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await this.initializeNative();
    } else {
      this.initializeBrowser();
    }
  }

  private async initializeNative(): Promise<void> {
    try {
      const status = await Network.getStatus();
      this.updateConnectionType(this.mapConnectionType(status.connectionType));
      this.rawStatus$.next(status.connected);

      this.networkListenerHandle = await Network.addListener('networkStatusChange', (s) => {
        this.rawStatus$.next(s.connected);
        this.updateConnectionType(this.mapConnectionType(s.connectionType));
      });

      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            this.lastForegroundTime = Date.now();
            this.restartPollTimers();
            this.clearResumePollTimer();
            this.resumePollTimer = setTimeout(() => {
              this.resumePollTimer = null;
              Network.getStatus()
                .then((s) => {
                  this.rawStatus$.next(s.connected);
                  this.updateConnectionType(this.mapConnectionType(s.connectionType));
                })
                .catch(() => this.rawStatus$.next(false));
            }, RESUME_GRACE_MS);
          } else {
            this.lastBackgroundTime = Date.now();
            this.clearResumePollTimer();
          }
        }).then((handle) => {
          this.appListenerHandle = handle;
        }).catch(() => {});
      }).catch(() => {});

      this.setupNativeFallbacks();
    } catch (error) {
      console.error('[NetworkService] Error initializing:', error);
      this.applyStatus(true, 'unknown');
    }
  }

  private setupNativeFallbacks(): void {
    if (typeof window === 'undefined') return;

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.lastForegroundTime = Date.now();
    });

    window.addEventListener('offline', () => this.rawStatus$.next(false));
    window.addEventListener('online', () => {
      Network.getStatus()
        .then((s) => this.rawStatus$.next(s.connected))
        .catch(() => this.rawStatus$.next(true));
    });

    this.startPollTimers();
  }

  private clearPollTimers(): void {
    if (this.nativePollTimer) {
      clearInterval(this.nativePollTimer);
      this.nativePollTimer = null;
    }
    if (this.navigatorOnlinePollTimer) {
      clearInterval(this.navigatorOnlinePollTimer);
      this.navigatorOnlinePollTimer = null;
    }
  }

  private startPollTimers(): void {
    this.clearPollTimers();
    this.nativePollTimer = setInterval(() => {
      Network.getStatus()
        .then((s) => this.rawStatus$.next(s.connected))
        .catch(() => {});
    }, NATIVE_POLL_INTERVAL_MS);

    this.navigatorOnlinePollTimer = setInterval(() => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        this.rawStatus$.next(false);
        return;
      }
      Network.getStatus()
        .then((s) => this.rawStatus$.next(s.connected))
        .catch(() => {});
    }, NAVIGATOR_ONLINE_POLL_MS);
  }

  private restartPollTimers(): void {
    this.startPollTimers();
  }

  private clearResumePollTimer(): void {
    if (this.resumePollTimer) {
      clearTimeout(this.resumePollTimer);
      this.resumePollTimer = null;
    }
  }

  private initializeBrowser(): void {
    const initialStatus = navigator.onLine;
    this.updateConnectionType(initialStatus ? 'wifi' : 'none');
    this.rawStatus$.next(initialStatus);

    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));
    merge(online$, offline$)
      .pipe(startWith(initialStatus))
      .subscribe((isOnline) => {
        this.updateConnectionType(isOnline ? 'wifi' : 'none');
        this.rawStatus$.next(isOnline);
      });
  }

  private getConnectionTypeFromStatus(): 'wifi' | 'cellular' | 'none' | 'unknown' {
    return this.statusSubject.value.connectionType ?? 'unknown';
  }

  private applyStatus(connected: boolean, connectionType: 'wifi' | 'cellular' | 'none' | 'unknown'): void {
    if (this.onlineSubject.value === connected) return;
    this.ngZone.run(() => {
      this.onlineSubject.next(connected);
      this.isOnlineSignal.set(connected);
      this.connectionTypeSignal.set(connectionType);
      this.statusSubject.next({
        connected,
        connectionType,
      });
    });
  }

  private updateConnectionType(connectionType: 'wifi' | 'cellular' | 'none' | 'unknown'): void {
    const current = this.statusSubject.value;
    if (current.connectionType === connectionType) return;
    this.connectionTypeSignal.set(connectionType);
    this.statusSubject.next({
      ...current,
      connectionType,
    });
  }

  private mapConnectionType(type: string | undefined): 'wifi' | 'cellular' | 'none' | 'unknown' {
    if (!type) return 'unknown';
    const normalized = type.toLowerCase();
    if (normalized === 'wifi' || normalized === 'ethernet') return 'wifi';
    if (['cellular', '4g', '5g', '3g'].includes(normalized)) return 'cellular';
    if (normalized === 'none' || normalized === 'disconnected') return 'none';
    return 'unknown';
  }

  /**
   * Manually trigger a network status re-check. Use when user taps "Retry".
   */
  async recheck(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      const online = navigator.onLine;
      this.rawStatus$.next(online);
      return online;
    }
    try {
      const status = await Network.getStatus();
      this.rawStatus$.next(status.connected);
      this.updateConnectionType(this.mapConnectionType(status.connectionType));
      return status.connected;
    } catch {
      this.rawStatus$.next(false);
      return false;
    }
  }

  isCurrentlyOnline(): boolean {
    return this.onlineSubject.value;
  }

  getCurrentConnectionType(): 'wifi' | 'cellular' | 'none' | 'unknown' {
    return this.connectionTypeSignal();
  }

  /**
   * Clean up listeners. Call when app is being destroyed (e.g. in platform destroy).
   */
  async destroy(): Promise<void> {
    this.clearResumePollTimer();
    if (this.networkListenerHandle) {
      await this.networkListenerHandle.remove();
      this.networkListenerHandle = null;
    }
    if (this.appListenerHandle) {
      await this.appListenerHandle.remove();
      this.appListenerHandle = null;
    }
    if (this.nativePollTimer) {
      clearInterval(this.nativePollTimer);
      this.nativePollTimer = null;
    }
    if (this.navigatorOnlinePollTimer) {
      clearInterval(this.navigatorOnlinePollTimer);
      this.navigatorOnlinePollTimer = null;
    }
  }
}
